"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { linkifyText } from "@/lib/utils/linkify-text";

interface ClampedTextProps {
  /** The full text to display. */
  text: string;
  /** Number of lines to show before clamping. Default 3. */
  lines?: number;
  /** Class applied to the text element. */
  className?: string;
  /** Render http(s) URLs in the text as clickable links (XSS-safe). */
  linkify?: boolean;
}

/**
 * Long free-text (notes, descriptions) rendered with a line clamp and an
 * in-place "Show more" / "Show less" toggle. The toggle only appears when the
 * text actually overflows the clamp — short text renders untouched. Presentation
 * only: the full text is always one click away, never truncated away.
 *
 * Uses inline `-webkit-line-clamp` (dynamic `lines`) so Tailwind JIT purge can't
 * drop the clamp class.
 */
export function ClampedText({ text, lines = 3, className, linkify = false }: ClampedTextProps) {
  const [expanded, setExpanded] = React.useState(false);
  const [overflowing, setOverflowing] = React.useState(false);
  const ref = React.useRef<HTMLParagraphElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Measured while clamped (initial render): full content taller than the
    // clamped box means the toggle is warranted.
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [text, lines]);

  const clampStyle: React.CSSProperties | undefined = expanded
    ? undefined
    : {
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

  return (
    <div>
      <p ref={ref} className={cn("whitespace-pre-wrap break-words", className)} style={clampStyle}>
        {linkify ? linkifyText(text) : text}
      </p>
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
